'use client'

import { Box, Button, Stack, ThemeProvider, Typography, createTheme } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import useOrgContext from '~/hooks/useOrgContext'
import UserThemeOptions from '~/layouts/UserThemeOptions'

import OrgBreadcrumb from './OrgBreadcrumb'

/** Hierarchy bar fill — exact RGB(19, 19, 19), not a theme surface token. */
const ORG_BAR_BG = 'rgb(19, 19, 19)'

/**
 * Fixed dark-chrome theme for the hierarchy bar. Session chrome keeps light
 * type in every appearance (Day / Night / Kinda Blue / Both) via the night
 * palette’s text tokens — the bar fill itself is the fixed ORG_BAR_BG chrome.
 *
 * Must use the same spacing scale as the app theme (`0.25rem` per unit, see
 * `@core/theme/ThemeOptions`) — bare `createTheme(UserThemeOptions(...))`
 * falls back to MUI’s 8px unit, so `px: 4` became 32px while the nav logo
 * stays at `spacing(4)` = 1rem and the first org no longer lined up.
 */
function useOrgBarTheme() {
  return useMemo(
    () =>
      createTheme({
        ...UserThemeOptions('dark'),
        spacing: (factor: number) => `${0.25 * factor}rem`
      }),
    []
  )
}

export default function OrgHierarchyBar() {
  const t = useTranslations('orgBar')
  const barTheme = useOrgBarTheme()
  const { homeOrg, path, children, isCloaking, canShowBar, isTreeLoading, switchTo, exitToHome } =
    useOrgContext()

  if (!canShowBar || !path.length) return null

  return (
    <ThemeProvider theme={barTheme}>
      <Box
        component="nav"
        aria-label={t('navLabel')}
        sx={{
          height: 42,
          // Use backgroundColor (not theme bgcolor path) so RGB(19,19,19) wins.
          backgroundColor: ORG_BAR_BG,
          color: 'text.primary',
          px: 4,
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Stack
          direction="row"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          minWidth={0}
        >
          <OrgBreadcrumb
            path={path}
            childOrgs={children}
            childrenLoading={isTreeLoading}
            onSelect={switchTo}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {isCloaking ? (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={exitToHome}
                aria-label={t('exitTo', { org: homeOrg.name })}
                sx={{
                  color: 'text.primary',
                  borderColor: 'text.disabled',
                  fontSize: (t) => t.typography.caption.fontSize,
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'text.secondary',
                    bgcolor: 'action.hover'
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'common.white',
                    outlineOffset: '2px'
                  }
                }}
              >
                {t('exitTo', { org: homeOrg.name })}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary" noWrap>
                {t('yourOrganization')}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    </ThemeProvider>
  )
}
